/**
 * Coinbase Exchange public WebSocket — Phase 53.F.
 *
 * Uses `wss://ws-feed.exchange.coinbase.com`, the fully public
 * market-data feed (no auth). Subscribing to the `ticker` channel
 * gives ~10-50 updates/sec on liquid pairs (much livelier than
 * Binance.US in quiet US market hours).
 *
 * Symbol translation:
 *   • Binance  format → BTCUSDT / ETHUSDC / SOLBUSD (no separator)
 *   • Coinbase format → BTC-USD / ETH-USDC              (hyphenated)
 *
 * The manager is API-compatible with `binanceStream.js` so the same
 * consumer patterns work. It exposes:
 *   • subscribe(symbols, kind='ticker')        → unsubscribe fn
 *   • addListener(fn)                          → detach fn
 *   • cache.ticker: Map<symbolCoinbase, row>
 */

const WS_URL = 'wss://ws-feed.exchange.coinbase.com';

/**
 * Translate a Binance-style symbol into Coinbase's hyphenated form.
 * Returns null when the pair almost certainly isn't listed on
 * Coinbase (e.g. BUSD-quoted pairs), so callers can skip cleanly.
 */
export function toCoinbaseSymbol(sym) {
  if (!sym) return null;
  const upper = sym.toUpperCase();
  const quotes = ['USDT', 'USDC', 'USD', 'BTC', 'ETH', 'EUR', 'GBP'];
  for (const q of quotes) {
    if (upper.endsWith(q)) {
      const base = upper.slice(0, -q.length);
      if (!base) return null;
      // Coinbase primarily uses USD (not USDT). Fold USDT→USD.
      const cbQuote = (q === 'USDT') ? 'USD' : q;
      return `${base}-${cbQuote}`;
    }
  }
  return null;
}

class CoinbaseStreamManager {
  constructor() {
    this._ws = null;
    this._connecting = false;
    this._products = new Map();        // productId -> refcount
    this._listeners = new Set();       // fn(msg)
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this.cache = { ticker: new Map(), trade: new Map() };
  }

  _connect() {
    if (this._ws || this._connecting) return;
    this._connecting = true;
    try { this._ws = new WebSocket(WS_URL); }
    catch { this._connecting = false; this._scheduleReconnect(); return; }

    this._ws.onopen = () => {
      this._connecting = false;
      this._reconnectAttempts = 0;
      const ids = [...this._products.keys()];
      if (ids.length) {
        this._send({ type: 'subscribe', channels: ['ticker'], product_ids: ids });
      }
    };
    this._ws.onmessage = (evt) => {
      let msg; try { msg = JSON.parse(evt.data); } catch { return; }
      if (msg.type === 'ticker' && msg.product_id) {
        const row = {
          symbol:      msg.product_id,
          last:        parseFloat(msg.price),
          open:        parseFloat(msg.open_24h),
          high:        parseFloat(msg.high_24h),
          low:         parseFloat(msg.low_24h),
          volume:      parseFloat(msg.volume_24h),
          side:        msg.side,
          ts:          Date.now(),
          time:        msg.time,
          bid:         parseFloat(msg.best_bid),
          ask:         parseFloat(msg.best_ask),
        };
        // Derive 24h change_pct on-the-fly.
        if (row.open && row.last) {
          row.change_pct = ((row.last - row.open) / row.open) * 100;
          row.change = row.last - row.open;
        }
        this.cache.ticker.set(msg.product_id, row);
        this._emit({ kind: 'ticker', symbol: msg.product_id, row });
      }
    };
    this._ws.onclose = () => {
      this._ws = null; this._connecting = false; this._scheduleReconnect();
    };
    this._ws.onerror = () => { try { this._ws?.close(); } catch { /* noop */ } };
  }

  _scheduleReconnect() {
    if (this._products.size === 0) return;
    if (this._reconnectTimer) return;
    const delay = Math.min(20000, 500 * Math.pow(2, this._reconnectAttempts++));
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connect();
    }, delay);
  }

  _send(o) {
    if (this._ws && this._ws.readyState === 1) {
      try { this._ws.send(JSON.stringify(o)); } catch { /* noop */ }
    }
  }

  _emit(msg) {
    for (const fn of this._listeners) { try { fn(msg); } catch { /* noop */ } }
  }

  addListener(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  /**
   * subscribe(symbols) — symbols can be Binance-style (BTCUSDT) or
   * Coinbase-style (BTC-USD). Unrecognized pairs are silently skipped.
   */
  subscribe(symbols /* , kind='ticker' */) {
    const cbIds = [];
    for (const s of symbols) {
      const id = s.includes('-') ? s.toUpperCase() : toCoinbaseSymbol(s);
      if (id) cbIds.push(id);
    }
    const uniq = [...new Set(cbIds)];
    const toSub = [];
    for (const id of uniq) {
      const cur = this._products.get(id) || 0;
      if (cur === 0) toSub.push(id);
      this._products.set(id, cur + 1);
    }
    if (!this._ws) this._connect();
    if (toSub.length && this._ws?.readyState === 1) {
      this._send({ type: 'subscribe', channels: ['ticker'], product_ids: toSub });
    }
    return () => {
      const toUnsub = [];
      for (const id of uniq) {
        const cur = this._products.get(id) || 0;
        if (cur <= 1) { this._products.delete(id); toUnsub.push(id); }
        else this._products.set(id, cur - 1);
      }
      if (toUnsub.length && this._ws?.readyState === 1) {
        this._send({ type: 'unsubscribe', channels: ['ticker'], product_ids: toUnsub });
      }
      if (this._products.size === 0 && this._ws) {
        try { this._ws.close(); } catch { /* noop */ }
        this._ws = null;
      }
    };
  }
}

export const coinbaseStream = new CoinbaseStreamManager();
